/*
 * Export URLs Panel - Shows copyable API URLs for top stories export
 * Forked from autobrr KeyField pattern
 */

import { ClipboardDocumentIcon, CheckIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

import { useFeedGroups } from "@api/queries";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { CollapsibleSection } from "@components/filters/_components";

interface UrlRowProps {
  label: string;
  url: string;
}

function UrlRow({ label, url }: UrlRowProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.custom((t) => (
        <Toast type="success" body={`${label} URL copied to clipboard`} t={t} />
      ));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.custom((t) => (
        <Toast type="error" body="Failed to copy to clipboard" t={t} />
      ));
    }
  };

  return (
    <div className="col-span-12">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <div className="flex rounded-md shadow-sm">
        <div className="relative flex items-stretch grow focus-within:z-10">
          <input
            type="text"
            readOnly
            value={url}
            className="block w-full rounded-none rounded-l-md border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-815 dark:text-gray-100 sm:text-sm font-mono focus:outline-none focus:border-blue-500 dark:focus:border-blue-500"
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-700 text-sm font-medium rounded-r-md text-gray-700 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
          title="Copy to clipboard"
        >
          {copied ? (
            <CheckIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
          ) : (
            <ClipboardDocumentIcon className="h-5 w-5 text-blue-500" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}

export function ExportUrlsPanel() {
  const { data: feedGroups } = useFeedGroups();

  const baseUrl = `${window.location.origin}/api/v1/export/top-stories`;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md mb-4 px-4">
      <CollapsibleSection
        title="Export URLs"
        subtitle="Copy top stories API URLs for integrations (n8n, webhooks, etc.)"
        defaultOpen={false}
        noBottomBorder
        childClassName="gap-y-4"
      >
        {/* All stories URL */}
        <UrlRow label="All Stories" url={baseUrl} />

        {/* Feed group URLs */}
        {feedGroups?.map((fg) => (
          <UrlRow
            key={fg.id}
            label={fg.name}
            url={`${baseUrl}?feed_group=${encodeURIComponent(fg.slug)}`}
          />
        ))}

        {/* Help text */}
        <div className="col-span-12 mt-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Use a logged-in Authorization header or create an X-API-Key integration token in Account Settings. {" "}
            Optional parameters: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">limit=10</code>{" "}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">since_hours=24</code>{" "}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">min_importance=0.5</code>{" "}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">category=slug</code>
          </p>
        </div>
      </CollapsibleSection>
    </div>
  );
}
